<script lang="ts" setup>
// src/components/app-sidebar/nav-team-manage.vue
// Team Management Dialog - View, Edit, and OAuth Management for Organizations
// Provides comprehensive organization management including details viewing, editing, OAuth reconnection, pricing, and staff configuration
// RELEVANT FILES: src/api/ringcentral-api.ts, src/stores/ringcentral.ts, src/types/ringcentral.ts, src/components/app-sidebar/team-switcher.vue

import { toTypedSchema } from '@vee-validate/zod';
import { useDebounceFn } from '@vueuse/core';
import { DollarSign, Loader2, Users as UsersIcon } from 'lucide-vue-next';
import { useForm } from 'vee-validate';
import { toast } from 'vue-sonner';
import * as z from 'zod';

import type { PricingConfig } from '@/types/organization-config';

import BasePricingConfigForm from '@/components/organization/BasePricingConfigForm.vue';
import BaseStaffConfigForm from '@/components/organization/BaseStaffConfigForm.vue';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TagsInput } from '@/components/ui/tags-input';
import { useAuthStore } from '@/stores/auth';
import { useOrganizationStore } from '@/stores/organization';
import { useRingCentralStore } from '@/stores/ringcentral';
import { trackIntegrationConnected } from '@/utils/ga4';
import { generateTimezoneOptions } from '@/utils/timezone';

const props = defineProps<{
  organizationId: string;
}>();

const emits = defineEmits(['close', 'preventClose']);

const authStore = useAuthStore();
const orgStore = useOrganizationStore();
const ringCentralStore = useRingCentralStore();

// Get organization details from store (reactive - will auto-update when store changes)
const organizationDetails = computed(() => {
  // Return the organization from store if it matches our organizationId
  if (ringCentralStore.organization?.organizationId === props.organizationId) {
    return ringCentralStore.organization;
  }
  // Otherwise, check in the organizations list
  return (
    ringCentralStore.organizations.find(org => org.organizationId === props.organizationId) || null
  );
});

const isLoading = ref(false);
const isUpdating = ref(false);
const error = ref<string | null>(null);

// Track active tab
const activeTab = ref('details');

// OAuth window tracking - define first before use in computed
const oauthWindow = ref<Window | null>(null);
const oauthStateId = ref<string | null>(null);
const oauthPollingInterval = ref<NodeJS.Timeout | null>(null);

// Dialog close control - always allow closing now
const hasFormChanges = ref(false);
const shouldPreventClose = computed(() => {
  // Always allow closing - user can close regardless of state
  return false;
});

// Watch for changes and emit to parent
watch(
  shouldPreventClose,
  prevent => {
    emits('preventClose', prevent);
  },
  { immediate: true },
);

// Email validation helper
const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Form validation schema
const organizationUpdateSchema = z.object({
  name: z
    .string()
    .min(1, 'Organization name is required')
    .max(100, 'Name must be less than 100 characters'),
  timezone: z.string().optional(),
  description: z.string().max(500, 'Description must be less than 500 characters').optional(),
  leadEmails: z.array(z.string().email()).default([]),
});

const formSchema = toTypedSchema(organizationUpdateSchema);

// Initialize timezone options from shared utility (static, no real-time updates)
const timezoneOptions = ref(generateTimezoneOptions());

// Form setup with initial values
const { handleSubmit, resetForm, meta } = useForm({
  validationSchema: formSchema,
  initialValues: {
    name: '',
    timezone: 'UTC',
    description: '',
    leadEmails: [],
  },
});

// Track form changes
watch(
  () => meta.value.dirty,
  isDirty => {
    hasFormChanges.value = isDirty;
  },
  { immediate: true },
);

// Watch for tab changes and reload form when switching to Settings
watch(activeTab, newTab => {
  if (newTab === 'settings' && organizationDetails.value) {
    // Reload form values when switching to Settings tab
    const orgData = organizationDetails.value;
    nextTick(() => {
      resetForm({
        values: {
          name: orgData.name || '',
          timezone: orgData.timezone || 'UTC',
          description: orgData.description || '',
          leadEmails: orgData.leadEmails ? [...orgData.leadEmails] : [],
        },
      });
    });
  }
});

// Load organization details from store (will update store state)
async function loadOrganizationDetails() {
  if (!props.organizationId) {return;}

  try {
    isLoading.value = true;
    error.value = null;

    // This will fetch from API and update the store automatically
    await ringCentralStore.getOrganization(props.organizationId);

    // organizationDetails is now a computed that reads from store
    // So it will automatically have the latest data

    // Reset form with loaded data
    await nextTick();

    resetForm({
      values: {
        name: organizationDetails.value?.name || '',
        timezone: organizationDetails.value?.timezone || 'UTC',
        description: organizationDetails.value?.description || '',
        leadEmails: organizationDetails.value?.leadEmails
          ? [...organizationDetails.value.leadEmails]
          : [],
      },
    });
  } catch (err) {
    console.error('❌ Failed to load organization details:', err);
    error.value = err instanceof Error ? err.message : 'Failed to load organization details';
  } finally {
    isLoading.value = false;
  }
}

// Update organization
const onSubmit = handleSubmit(async values => {
  if (!props.organizationId) {return;}

  try {
    isUpdating.value = true;
    error.value = null;

    const updateData = {
      name: values.name,
      timezone: values.timezone || 'UTC',
      description: values.description || undefined,
      leadEmails: values.leadEmails || [],
    };

    // Use store's updateOrganization - it will update the store state automatically
    await ringCentralStore.updateOrganization(props.organizationId, updateData);

    // organizationDetails computed will automatically reflect the updated store state

    // Refresh organizations data in stores to update UI
    await Promise.all([
      ringCentralStore.getUserOrganizations(true),
      authStore.fetchUserMemberships(true),
      orgStore.loadUserOrganizations(),
    ]);

    toast.success('Organization updated successfully!', {
      position: 'top-center',
    });

  } catch (err) {
    console.error('❌ Failed to update organization:', err);
    error.value = err instanceof Error ? err.message : 'Failed to update organization';
    toast.error('Failed to update organization', {
      position: 'top-center',
      description: error.value,
    });
  } finally {
    isUpdating.value = false;
  }
});

// Start OAuth flow
async function startOAuth(accessLevel: 'account' | 'extension' = 'account') {
  if (!props.organizationId) {return;}

  try {
    // Use RingCentral store to initiate OAuth
    const result = await ringCentralStore.initiateOAuth(props.organizationId, accessLevel);
    oauthWindow.value = result.authWindow;
    oauthStateId.value = result.stateId;

    if (!oauthWindow.value) {
      throw new Error('Unable to open authorization window. Please allow popups for this site.');
    }

    toast.info('Complete authorization in the popup window', {
      position: 'top-center',
      description: "We'll automatically detect when authorization is complete.",
    });

    // Start polling OAuth status
    startOAuthPolling();
  } catch (err) {
    console.error('Failed to start OAuth:', err);
    toast.error('Failed to start RingCentral authorization', {
      position: 'top-center',
      description: err instanceof Error ? err.message : 'An unexpected error occurred',
    });
  }
}

// Start OAuth status polling
function startOAuthPolling() {
  if (!oauthStateId.value) {return;}

  const checkStatus = async () => {
    try {
      const status = await ringCentralStore.checkOAuthStatus(oauthStateId.value!);

      if (status.status === 'PENDING') {
        // Continue polling
      } else if (status.status === 'SUCCESS') {
        handleOAuthSuccess();
      } else if (status.status === 'DUPLICATE') {
        handleOAuthDuplicate(status.providerAccountId);
      } else if (status.status === 'FAILED') {
        handleOAuthError(status.errorMessage || 'Authorization failed');
      }
    } catch (err) {
      console.error('Error checking OAuth status:', err);
      // Don't stop polling on error, might be temporary
    }
  };

  // Initial check
  checkStatus();

  // Set up polling every 2 seconds
  oauthPollingInterval.value = setInterval(checkStatus, 2000);
}

// Handle OAuth success
function handleOAuthSuccess() {
  stopOAuthPolling();

  // Close OAuth window
  if (oauthWindow.value && !oauthWindow.value.closed) {
    oauthWindow.value.close();
  }
  oauthWindow.value = null;
  oauthStateId.value = null;

  // Track integration connected in GA4 (key activation metric)
  trackIntegrationConnected(props.organizationId);

  toast.success('RingCentral connected successfully!', {
    position: 'top-center',
    description: 'Your organization is now connected to RingCentral.',
  });

  // Reload organization details to show updated status
  loadOrganizationDetails();
}

// Handle OAuth duplicate account
function handleOAuthDuplicate(providerAccountId?: string) {
  stopOAuthPolling();

  // Close OAuth window
  if (oauthWindow.value && !oauthWindow.value.closed) {
    oauthWindow.value.close();
  }
  oauthWindow.value = null;
  oauthStateId.value = null;

  toast.error('Account already connected', {
    position: 'top-center',
    description: providerAccountId
      ? `RingCentral account ${providerAccountId} is already connected to another organization.`
      : 'This RingCentral account is already connected to another organization.',
  });
}

// Handle OAuth error
function handleOAuthError(errorMessage: string) {
  stopOAuthPolling();

  // Close OAuth window
  if (oauthWindow.value && !oauthWindow.value.closed) {
    oauthWindow.value.close();
  }
  oauthWindow.value = null;
  oauthStateId.value = null;

  toast.error('Authorization failed', {
    position: 'top-center',
    description: errorMessage,
  });
}

// Stop OAuth polling
function stopOAuthPolling() {
  if (oauthPollingInterval.value) {
    clearInterval(oauthPollingInterval.value);
    oauthPollingInterval.value = null;
  }
}

// Retry OAuth
function retryOAuth() {
  // Stop any existing polling
  stopOAuthPolling();

  // Close existing OAuth window if open
  if (oauthWindow.value && !oauthWindow.value.closed) {
    oauthWindow.value.close();
  }
  oauthWindow.value = null;
  oauthStateId.value = null;

  // Restart OAuth flow
  startOAuth();
}

// Format date for display
function formatDate(dateString: string) {
  if (!dateString) {return 'N/A';}
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ============ Pricing and Staff Configuration ============

// State for pricing and staff updates
const isUpdatingConfig = ref(false);

// Computed properties for pricing and staff
const hasPricingConfig = computed(() => {
  const pricing = organizationDetails.value?.businessConfig?.pricing;
  if (!pricing) {return false;}
  return (
    (pricing.membershipTiers && pricing.membershipTiers.length > 0) ||
    (pricing.classPacks && pricing.classPacks.length > 0) ||
    !!pricing.introOffer ||
    !!pricing.addOns
  );
});

const hasStaff = computed(() => {
  return (
    organizationDetails.value?.businessConfig?.staff &&
    organizationDetails.value.businessConfig.staff.trim().length > 0
  );
});

// Local ref for staff names - updates instantly in UI
const localStaffNames = ref<string[]>([]);

// Track the last saved staff string to prevent unnecessary API calls
const lastSavedStaffString = ref('');

// Initialize local staff names from organization data
watch(
  () => organizationDetails.value?.businessConfig?.staff,
  staffString => {
    // Save the original staff string from store
    lastSavedStaffString.value = staffString || '';

    if (!staffString) {
      localStaffNames.value = [];
      return;
    }
    localStaffNames.value = staffString
      .split('\n')
      .map((name: string) => name.trim())
      .filter((name: string) => name.length > 0);
  },
  { immediate: true },
);

// Debounced save function - only calls API after 500ms of inactivity
const debouncedSave = useDebounceFn((names: string[]) => {
  const staffString = names.join('\n');
  handleStaffUpdate(staffString);
}, 500);

// Watch for changes and trigger debounced save
// Only save if the value actually changed from what's in the store
watch(
  localStaffNames,
  newNames => {
    const currentString = newNames.join('\n');
    // Only trigger save if user actually changed the value
    if (currentString !== lastSavedStaffString.value) {
      debouncedSave(newNames);
    }
  },
  { deep: true },
);

// Clean staff names (trim, deduplicate)
function cleanStaffNames(staffString: string): string {
  if (!staffString || !staffString.trim()) {return '';}

  const names = staffString
    .split('\n')
    .map(name => name.trim())
    .filter(name => name.length > 0);

  const seen = new Set<string>();
  const uniqueNames: string[] = [];
  for (const name of names) {
    const lower = name.toLowerCase();
    if (!seen.has(lower)) {
      seen.add(lower);
      uniqueNames.push(name);
    }
  }

  return uniqueNames.join('\n');
}

// Handle pricing configuration update
async function handlePricingUpdate(data: PricingConfig) {
  if (!props.organizationId || !organizationDetails.value) {return;}

  try {
    isUpdatingConfig.value = true;

    // Clean the pricing data - filter out empty entries
    const cleanedData: PricingConfig = {
      membershipTiers: data.membershipTiers?.filter(tier => tier.name.trim() !== '') || [],
      classPacks: data.classPacks?.filter(pack => pack.name.trim() !== '') || [],
      introOffer: data.introOffer,
      addOns: data.addOns,
    };


    // Use store's updateOrganization - it will update the store state automatically
    await ringCentralStore.updateOrganization(props.organizationId, {
      businessConfig: {
        pricing: cleanedData,
      },
    });

    // organizationDetails computed will automatically reflect the updated store state
    // No need to manually assign - the computed will pick up the change

    // Refresh other stores in background
    Promise.all([authStore.fetchUserMemberships(true), orgStore.loadUserOrganizations()]).catch(
      err => console.error('Failed to refresh stores:', err),
    );

    toast.success('Pricing configuration updated', {
      position: 'top-center',
    });

  } catch (err) {
    console.error('❌ Failed to update pricing configuration:', err);
    toast.error('Failed to update pricing configuration', {
      position: 'top-center',
      description: err instanceof Error ? err.message : 'An unexpected error occurred',
    });
  } finally {
    isUpdatingConfig.value = false;
  }
}

// Handle staff configuration update
async function handleStaffUpdate(data: string) {
  if (!props.organizationId || !organizationDetails.value) {return;}

  try {
    isUpdatingConfig.value = true;

    const cleanedStaff = cleanStaffNames(data);


    // Use store's updateOrganization - it will update the store state automatically
    await ringCentralStore.updateOrganization(props.organizationId, {
      businessConfig: {
        staff: cleanedStaff,
      },
    });

    // organizationDetails computed will automatically reflect the updated store state
    // No need to manually assign - the computed will pick up the change

    // Refresh other stores in background
    Promise.all([
      ringCentralStore.getUserOrganizations(true),
      authStore.fetchUserMemberships(true),
      orgStore.loadUserOrganizations(),
    ]).catch(err => console.error('Failed to refresh stores:', err));

    toast.success('Staff configuration updated', {
      position: 'top-center',
    });

  } catch (err) {
    console.error('❌ Failed to update staff configuration:', err);
    toast.error('Failed to update staff configuration', {
      position: 'top-center',
      description: err instanceof Error ? err.message : 'An unexpected error occurred',
    });
  } finally {
    isUpdatingConfig.value = false;
  }
}

const FRANCHISE_LABELS: Record<string, string> = {
  orangeTheory: 'Orange Theory',
};

// Load details when component mounts
onMounted(() => {
  loadOrganizationDetails();
});

// Cleanup on unmount
onUnmounted(() => {
  // Stop OAuth polling
  stopOAuthPolling();

  // Close OAuth window if still open
  if (oauthWindow.value && !oauthWindow.value.closed) {
    oauthWindow.value.close();
  }
});
</script>

<template>
  <div class="flex flex-col h-full max-h-[calc(85vh-3rem)]">
    <!-- Fixed header - doesn't scroll -->
    <div class="flex-shrink-0 border-b pb-4 mb-4">
      <UiDialogHeader>
        <UiDialogTitle> Manage Organization </UiDialogTitle>
        <UiDialogDescription>
          View details, update settings, and manage RingCentral connection for your organization.
        </UiDialogDescription>
      </UiDialogHeader>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="flex items-center justify-center py-8">
      <div class="flex items-center space-x-2">
        <Loader2 class="h-4 w-4 animate-spin text-primary" />
        <span class="text-sm text-muted-foreground">Loading organization details...</span>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="rounded-lg bg-red-50 border border-red-200 p-4 mb-6">
      <div class="flex items-start">
        <div class="flex-1">
          <p class="text-sm text-red-800">
            {{ error }}
          </p>
        </div>
      </div>
      <div class="mt-3">
        <UiButton variant="outline" size="sm" @click="loadOrganizationDetails">
          Try Again
        </UiButton>
      </div>
    </div>

    <!-- Main Content with Fixed Tabs -->
    <Tabs
      v-else-if="organizationDetails"
      v-model="activeTab"
      default-value="details"
      class="flex flex-col flex-1 min-h-0"
    >
      <!-- Fixed Tabs Header -->
      <TabsList class="grid w-full grid-cols-5 flex-shrink-0 mb-4">
        <TabsTrigger value="details"> Details </TabsTrigger>
        <TabsTrigger value="settings"> Settings </TabsTrigger>
        <TabsTrigger value="pricing"> Pricing </TabsTrigger>
        <TabsTrigger value="staff"> Staff </TabsTrigger>
        <TabsTrigger value="oauth"> OAuth </TabsTrigger>
      </TabsList>

      <!-- Scrollable Tab Content -->
      <div class="flex-1 overflow-y-auto min-h-0 -mx-6 px-6 py-1">
        <!-- Details Tab -->
        <TabsContent value="details" class="mt-6">
          <div class="space-y-6">
            <!-- Organization Info -->
            <div class="grid grid-cols-2 gap-6">
              <div class="space-y-2">
                <label class="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                  >Name</label
                >
                <p class="text-sm font-medium text-foreground">
                  {{ organizationDetails.name || 'N/A' }}
                </p>
              </div>
            </div>

            <!-- Timestamps -->
            <div class="grid grid-cols-2 gap-6">
              <div class="space-y-2">
                <label class="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                  >Created</label
                >
                <p class="text-sm text-foreground">
                  {{ formatDate(organizationDetails.createdAt) }}
                </p>
              </div>
              <div class="space-y-2">
                <label class="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                  >Last Updated</label
                >
                <p class="text-sm text-foreground">
                  {{ formatDate(organizationDetails.updatedAt || '') }}
                </p>
              </div>
            </div>

            <!-- Settings & Status -->
            <div class="grid grid-cols-2 gap-6">
              <div class="space-y-2">
                <label class="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                  >Franchise</label
                >
                <p class="text-sm text-foreground">
                  {{
                    FRANCHISE_LABELS[organizationDetails.franchise] || organizationDetails.franchise
                  }}
                </p>
              </div>
              <div class="space-y-2">
                <label class="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                  >Timezone</label
                >
                <p class="text-sm text-foreground">
                  {{ organizationDetails.timezone || 'N/A' }}
                </p>
              </div>
            </div>

            <!-- RingCentral Status -->
            <div class="grid grid-cols-2 gap-6">
              <div class="space-y-2">
                <label class="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                  >RingCentral Status</label
                >
                <div class="flex items-center gap-2">
                  <div
                    class="w-2 h-2 rounded-full"
                    :class="
                      organizationDetails.oauthConnection?.hasOAuth &&
                      organizationDetails.oauthConnection?.status === 'ACTIVE'
                        ? 'bg-green-500'
                        : 'bg-red-500'
                    "
                  />
                  <span class="text-sm font-medium text-foreground">{{
                    organizationDetails.oauthConnection?.hasOAuth &&
                    organizationDetails.oauthConnection?.status === 'ACTIVE'
                      ? 'Connected'
                      : 'Not Connected'
                  }}</span>
                </div>
              </div>
            </div>

            <!-- Optional Fields -->
            <div
              v-if="organizationDetails.description"
              class="space-y-4 pt-2 border-t border-border"
            >
              <div class="space-y-2">
                <label class="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                  >Description</label
                >
                <p class="text-sm text-foreground leading-relaxed">
                  {{ organizationDetails.description }}
                </p>
              </div>
            </div>
          </div>
        </TabsContent>

        <!-- Settings Tab -->
        <TabsContent value="settings" class="mt-6">
          <form class="space-y-6" @submit="onSubmit">
            <FormField v-slot="{ componentField }" name="name">
              <FormItem>
                <FormLabel class="text-base">
                  Organization Name <span class="text-red-500">*</span>
                </FormLabel>
                <FormControl>
                  <UiInput
                    v-bind="componentField"
                    :disabled="isUpdating"
                    placeholder="Enter organization name"
                  />
                </FormControl>
                <FormDescription> The display name for your organization. </FormDescription>
                <FormMessage />
              </FormItem>
            </FormField>

            <FormField v-slot="{ componentField }" name="timezone">
              <FormItem>
                <FormLabel class="text-base"> Timezone </FormLabel>
                <FormControl>
                  <Select v-bind="componentField" :disabled="isUpdating">
                    <SelectTrigger>
                      <SelectValue placeholder="Select timezone" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem
                        v-for="option in timezoneOptions"
                        :key="option.value"
                        :value="option.value"
                      >
                        {{ option.label }}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormDescription>
                  The timezone for your organization's operations.
                </FormDescription>
                <FormMessage />
              </FormItem>
            </FormField>

            <FormField v-slot="{ componentField }" name="description">
              <FormItem>
                <FormLabel class="text-base"> Description </FormLabel>
                <FormControl>
                  <UiTextarea
                    v-bind="componentField"
                    :disabled="isUpdating"
                    placeholder="Describe your organization..."
                    class="min-h-[80px]"
                  />
                </FormControl>
                <FormDescription>
                  Optional description of your organization (max 500 characters).
                </FormDescription>
                <FormMessage />
              </FormItem>
            </FormField>

            <FormField v-slot="{ value, handleChange }" name="leadEmails">
              <FormItem>
                <FormLabel class="text-base"> Lead Emails </FormLabel>
                <FormControl>
                  <TagsInput
                    :model-value="value"
                    :disabled="isUpdating"
                    placeholder="Add lead emails (e.g., manager@example.com)..."
                    :validate-tag="isValidEmail"
                    @update:model-value="handleChange"
                  />
                </FormControl>
                <FormDescription>
                  Email addresses for leads associated with this organization. Must be valid email
                  format.
                </FormDescription>
                <FormMessage />
              </FormItem>
            </FormField>

            <div class="flex justify-end space-x-3 pt-4 border-t border-border">
              <UiButton
                type="button"
                variant="outline"
                :disabled="isUpdating"
                @click="loadOrganizationDetails"
              >
                Reset
              </UiButton>
              <UiButton type="submit" :loading="isUpdating" :disabled="isUpdating">
                <template v-if="isUpdating"> Saving... </template>
                <template v-else> Save Changes </template>
              </UiButton>
            </div>
          </form>
        </TabsContent>

        <!-- Pricing Tab -->
        <TabsContent value="pricing" class="mt-6">
          <div class="space-y-6">
            <!-- Empty state or form -->
            <div v-if="!hasPricingConfig" class="text-center py-12">
              <div
                class="mx-auto w-16 h-16 mb-4 flex items-center justify-center rounded-full bg-blue-50"
              >
                <DollarSign class="h-8 w-8 text-blue-500" />
              </div>
              <h4 class="text-base font-medium text-gray-900 mb-2">
                Create Your First Pricing Tier
              </h4>
              <p class="text-sm text-gray-600 mb-6 max-w-md mx-auto">
                Define monthly memberships to offer recurring subscriptions to your clients.
              </p>
            </div>

            <!-- Pricing Form -->
            <BasePricingConfigForm
              :initial-data="organizationDetails?.businessConfig?.pricing"
              :is-submitting="isUpdatingConfig"
              submit-label="Save Pricing"
              @submit="handlePricingUpdate"
              @cancel="() => {}"
            />
          </div>
        </TabsContent>

        <!-- Staff Tab -->
        <TabsContent value="staff" class="mt-6">
          <div class="space-y-6">
            <!-- Current staff display -->
            <div v-if="hasStaff" class="space-y-4">
              <div>
                <h4 class="text-sm font-medium mb-3">Current Staff Members</h4>
                <TagsInput
                  v-model="localStaffNames"
                  placeholder="Add staff member..."
                  class="mb-3"
                />
                <p class="text-xs text-muted-foreground">
                  {{ localStaffNames.length }} staff member{{
                    localStaffNames.length !== 1 ? 's' : ''
                  }}
                  configured for call tracking
                </p>
              </div>
            </div>

            <!-- Empty state -->
            <div v-else class="text-center py-8">
              <div
                class="mx-auto w-16 h-16 mb-4 flex items-center justify-center rounded-full bg-blue-50"
              >
                <UsersIcon class="h-8 w-8 text-blue-500" />
              </div>
              <h4 class="text-base font-medium text-gray-900 mb-2">Add Your Team Members</h4>
              <p class="text-sm text-gray-600 mb-4 max-w-md mx-auto">
                Enable individual performance tracking and identify coaching opportunities for each
                team member.
              </p>
            </div>

            <!-- Staff Form -->
            <BaseStaffConfigForm
              :initial-data="organizationDetails?.businessConfig?.staff"
              :is-submitting="isUpdatingConfig"
              submit-label="Save Staff"
              @submit="handleStaffUpdate"
              @cancel="() => {}"
            />
          </div>
        </TabsContent>

        <!-- OAuth Tab -->
        <TabsContent value="oauth" class="mt-6">
          <div class="space-y-6">
            <!-- Current Status -->
            <div class="rounded-lg border p-4">
              <div class="flex items-center justify-between">
                <div>
                  <h3 class="font-medium">RingCentral Connection</h3>
                  <p class="text-sm text-muted-foreground">
                    {{
                      organizationDetails.hasOAuth
                        ? 'Your organization is connected to RingCentral'
                        : 'Connect to RingCentral to enable call analytics'
                    }}
                  </p>
                </div>
                <div class="flex items-center space-x-2">
                  <div
                    class="w-3 h-3 rounded-full"
                    :class="
                      organizationDetails.oauthConnection?.hasOAuth &&
                      organizationDetails.oauthConnection?.status === 'ACTIVE'
                        ? 'bg-green-500'
                        : 'bg-red-500'
                    "
                  />
                  <span class="text-sm font-medium">
                    {{
                      organizationDetails.oauthConnection?.hasOAuth &&
                      organizationDetails.oauthConnection?.status === 'ACTIVE'
                        ? 'Connected'
                        : 'Not Connected'
                    }}
                  </span>
                </div>
              </div>
            </div>

            <!-- OAuth Actions -->
            <div class="space-y-4">
              <div v-if="!organizationDetails.hasOAuth" class="text-center py-4">
                <div
                  class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-primary/10 mb-4"
                >
                  <svg
                    class="h-6 w-6 text-primary"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                </div>
                <h3 class="text-lg font-medium mb-2">Connect to RingCentral</h3>
                <p class="text-sm text-muted-foreground mb-6">
                  Authorize access to your RingCentral account to enable call analytics and
                  reporting.
                </p>

                <div class="flex flex-col gap-3">
                  <!-- Two OAuth Buttons -->
                  <div v-if="!oauthWindow" class="flex flex-col gap-2">
                    <UiButton class="w-full" @click="startOAuth('account')">
                      <svg class="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                        <path
                          d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
                        />
                      </svg>
                      Account Level (Recommended)
                    </UiButton>
                    <UiButton class="w-full" @click="startOAuth('extension')">
                      <svg class="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                        <path
                          d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
                        />
                      </svg>
                      Extension Level
                    </UiButton>
                    <p class="text-xs text-gray-500 mt-1">
                      Need all calls? Choose account-level. Need calls from just one extension?
                      Choose extension-level.
                    </p>
                  </div>

                  <!-- Authorization in progress -->
                  <div v-else class="space-y-3">
                    <div class="flex items-center justify-center p-4 bg-blue-50 rounded-lg">
                      <div class="flex items-center space-x-2">
                        <Loader2 class="h-4 w-4 animate-spin text-blue-600" />
                        <span class="text-sm text-blue-800">Waiting for authorization...</span>
                      </div>
                    </div>
                    <UiButton variant="outline" class="w-full" @click="retryOAuth">
                      <svg class="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                        <path
                          d="M12 6v3l4-4-4-4v3c-4.42 0-8 3.58-8 8 0 1.57.46 3.03 1.24 4.26L6.7 14.8c-.45-.83-.7-1.79-.7-2.8 0-3.31 2.69-6 6-6zm6.76 1.74L17.3 9.2c.44.84.7 1.79.7 2.8 0 3.31-2.69 6-6 6v-3l-4 4 4 4v-3c4.42 0 8-3.58 8-8 0-1.57-.46-3.03-1.24-4.26z"
                        />
                      </svg>
                      Retry Authorization
                    </UiButton>
                  </div>
                </div>
              </div>

              <!-- Already Connected -->
              <div v-else class="text-center py-4">
                <div
                  class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4"
                >
                  <svg
                    class="h-6 w-6 text-green-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <h3 class="text-lg font-medium mb-2">Connected Successfully</h3>
                <p class="text-sm text-muted-foreground mb-6">
                  Your RingCentral account is connected and call analytics are enabled.
                </p>

                <div class="flex flex-col gap-2">
                  <UiButton class="w-full" @click="startOAuth('account')">
                    <svg class="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                      <path
                        d="M12 6v3l4-4-4-4v3c-4.42 0-8 3.58-8 8 0 1.57.46 3.03 1.24 4.26L6.7 14.8c-.45-.83-.7-1.79-.7-2.8 0-3.31 2.69-6 6-6zm6.76 1.74L17.3 9.2c.44.84.7 1.79.7 2.8 0 3.31-2.69 6-6 6v-3l-4 4 4 4v-3c4.42 0 8-3.58 8-8 0-1.57-.46-3.03-1.24-4.26z"
                      />
                    </svg>
                    Reconnect (Account Level)
                  </UiButton>
                  <UiButton class="w-full" @click="startOAuth('extension')">
                    <svg class="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                      <path
                        d="M12 6v3l4-4-4-4v3c-4.42 0-8 3.58-8 8 0 1.57.46 3.03 1.24 4.26L6.7 14.8c-.45-.83-.7-1.79-.7-2.8 0-3.31 2.69-6 6-6zm6.76 1.74L17.3 9.2c.44.84.7 1.79.7 2.8 0 3.31-2.69 6-6 6v-3l-4 4 4 4v-3c4.42 0 8-3.58 8-8 0-1.57-.46-3.03-1.24-4.26z"
                      />
                    </svg>
                    Reconnect (Extension Level)
                  </UiButton>
                  <p class="text-xs text-gray-500 mt-1">
                    Need all calls? Choose account-level. Need calls from just one extension? Choose
                    extension-level.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>
      </div>
    </Tabs>
  </div>
</template>

<style scoped></style>
